/**
 * Shared helpers for evaluating candidate screening profile values
 * against the per-company screening preferences (importance flag +
 * preferred answers). Used by both the inline `ScreeningProfileEditor`
 * borders and the top-of-profile `ScreeningScorecard` widget.
 *
 * Preferences shape: { importance: "optional"|"important",
 *   preferred: string[], preferred_min?: number, preferred_max?: number }
 *
 * Status:
 *   - "empty-important": empty AND marked important → amber highlight
 *   - "preferred":       value matches a preferred answer → green highlight
 *   - "not-preferred":   value present AND doesn't match preference → red highlight
 *   - "neutral":         no pref configured, or filled but no pref set
 */
export function evaluateFieldStatus(value, pref) {
    const isEmpty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    if (!pref) return "neutral";
    if (isEmpty) return pref.importance === "important" ? "empty-important" : "neutral";
    // Numeric range check
    if (typeof value === "number" || (typeof value === "string" && value !== "" && !isNaN(Number(value)) && (pref.preferred_min != null || pref.preferred_max != null))) {
        const n = Number(value);
        const okMin = pref.preferred_min == null || n >= pref.preferred_min;
        const okMax = pref.preferred_max == null || n <= pref.preferred_max;
        if (pref.preferred_min != null || pref.preferred_max != null) {
            return okMin && okMax ? "preferred" : "not-preferred";
        }
    }
    // String / select match
    if (Array.isArray(pref.preferred) && pref.preferred.length > 0) {
        const norm = (s) => String(s).trim().toLowerCase();
        const target = norm(value);
        const set = pref.preferred.map(norm);
        return set.includes(target) ? "preferred" : "not-preferred";
    }
    return "neutral";
}

/**
 * Roll up screening profile + preferences into scorecard buckets.
 * Only counts fields that have SOME preference configured — fields
 * with no `prefs[key]` entry are silently ignored so counts stay
 * meaningful for hiring managers.
 *
 * Returns { preferred, notPreferred, needsAttention, configured,
 *   firstByStatus: { preferred: key, notPreferred: key, needsAttention: key } }
 */
export function computeScreeningScorecard(profile, prefs, fields) {
    const counts = { preferred: 0, notPreferred: 0, needsAttention: 0, configured: 0 };
    const firstByStatus = { preferred: null, "not-preferred": null, "empty-important": null };
    const orderedFields = Array.isArray(fields) ? fields : (fields?.fields || []);
    orderedFields.forEach((f) => {
        const p = prefs?.[f.key];
        if (!p) return;
        counts.configured++;
        const status = evaluateFieldStatus(profile?.[f.key], p);
        if (status === "preferred") {
            counts.preferred++;
            if (!firstByStatus.preferred) firstByStatus.preferred = f.key;
        } else if (status === "not-preferred") {
            counts.notPreferred++;
            if (!firstByStatus["not-preferred"]) firstByStatus["not-preferred"] = f.key;
        } else if (status === "empty-important") {
            counts.needsAttention++;
            if (!firstByStatus["empty-important"]) firstByStatus["empty-important"] = f.key;
        }
    });
    return { ...counts, firstByStatus };
}
